import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse } from "antd";
import Steps from "antd/es/steps";
import { useIntl } from "react-intl";

import { EntityTypesSupportedByImport } from "@mapmycustomers/shared/types/entity";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import ImportMapping from "@mapmycustomers/shared/types/imports/ImportMapping";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, LoadingSpinner } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";
import FilePreview from "@app/types/FilePreview";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getByEntityType from "@app/util/fieldModel/getByEntityType";

import SettingPage from "../../common/SettingPage";
import StepsEnum from "../enum/Steps";
import { initializeAddPage, setStep } from "../store/actions";
import {
  getEntityType,
  getFilePreview,
  getMappedColumns,
  getSelectedConfig,
  getStep,
  isAddPageLoading,
  isConfigModified,
} from "../store/selectors";
import getStepNames from "../util/getStepNames";
import isFieldRequiredInImports from "../util/isFieldRequired";

import ButtonBar from "./components/ButtonBar";
import FieldMappingStep from "./components/FieldMappingStep";
import CreateConfigModal from "./components/FieldMappingStep/Config/modals/CreateConfigModal";
import FinalizeStep from "./components/FinalizeStep";
import LayoutSelectorModal from "./components/LayoutSelectorModal";
import UploadStep from "./components/UploadStep";
import GetStartedSection from "./GetStartedSection";
import messages from "./messages";
import styles from "./NewImport.module.scss";
import PageContainer from "./PageContainer";
import StepInfo from "./StepInfo";

interface Props {
  entityType: EntityTypesSupportedByImport;
  filePreview?: FilePreview;
  initialize: typeof initializeAddPage.request;
  isConfigModified: boolean;
  isNewConfig: boolean;
  loading: boolean;
  mappedColumns: ImportMapping;
  setStep: typeof setStep;
  step: StepsEnum;
}

const NewImport: React.FC<Props> = ({
  entityType,
  filePreview,
  initialize,
  isConfigModified,
  isNewConfig,
  loading,
  mappedColumns,
  setStep,
  step,
}) => {
  const intl = useIntl();
  const [visible, showInfo, hideInfo] = useBoolean();
  const [layoutSelectorVisible, showLayoutSelector, hideLayoutSelector] = useBoolean();

  const steps = getStepNames(intl);
  const [createConfigModalVisible, showCreateConfigModal, hideCreateConfigModal] = useBoolean();

  useEffect(() => {
    initialize();
  }, [initialize]);

  const importAddAnalytics = useExtendedAnalytics(
    ["Settings", "Data", "New Import"],
    useAnalytics()
  );

  const handleNextStep = useCallback(() => {
    if (step === StepsEnum.UPLOAD) {
      showLayoutSelector();
    } else {
      const nextStep = step < StepsEnum.FINALIZE ? step + 1 : step;
      setStep(nextStep);
      importAddAnalytics.clicked([`Continue to step ${nextStep + 1}`]);
    }
  }, [importAddAnalytics, setStep, showLayoutSelector, step]);

  const handlePreviousStep = useCallback(() => {
    const nextStep = step > StepsEnum.UPLOAD ? step - 1 : step;
    setStep(nextStep);
    importAddAnalytics.clicked([`Back to step ${nextStep + 1}`]);
  }, [importAddAnalytics, setStep, step]);

  const handleHideLayoutSelector = useCallback(
    (layoutSelected?: boolean) => {
      hideLayoutSelector();
      if (layoutSelected) {
        setStep(step + 1);
      }
    },
    [hideLayoutSelector, step, setStep]
  );

  const nextStepDisabled = useMemo(() => {
    if (step === StepsEnum.UPLOAD && (!filePreview || filePreview.isErrored)) {
      return true;
    }
    if (step === StepsEnum.MATCH_FIELDS) {
      const fieldModel: IFieldModel = getByEntityType(entityType);

      if (
        fieldModel.fields
          .filter(isFieldRequiredInImports)
          .some((field) => !mappedColumns[field.importName])
      ) {
        return true;
      }
    }
    return false;
  }, [entityType, filePreview, mappedColumns, step]);

  const nextButtonErrorMessage = useMemo(() => {
    if (step === StepsEnum.UPLOAD) {
      return intl.formatMessage({
        id: "settings.newImports.error.noFile",
        defaultMessage: "You must upload a file to continue",
        description: "Import step 1 error message",
      });
    }
    if (step === StepsEnum.MATCH_FIELDS) {
      return intl.formatMessage(messages.matchRequired);
    }
    return undefined;
  }, [step, intl]);

  const nextButtonConfirmationMessage = useMemo(() => {
    if (step === StepsEnum.MATCH_FIELDS) {
      if (isNewConfig) {
        return (
          <div>
            <div className={styles.saveNewMatchingTitle}>
              {intl.formatMessage(messages.saveNewMatchingTitle)}
            </div>
            <div className={styles.saveNewMatchingDescription}>
              {intl.formatMessage(messages.saveNewMatchingDescription)}
            </div>
          </div>
        );
      } else if (isConfigModified) {
        return intl.formatMessage(messages.changeStepConfirmation);
      }
    }
    return undefined;
  }, [intl, isConfigModified, isNewConfig, step]);

  const handleCancelConfigCreation = useCallback(() => {
    hideCreateConfigModal();
    handleNextStep();
  }, [hideCreateConfigModal, handleNextStep]);

  const [activePanelKey, setActivePanelKey] = useState<string | string[] | undefined>(undefined);

  const handleCollapsePanelClick = useCallback(() => {
    setActivePanelKey(activePanelKey ? undefined : "1");
  }, [activePanelKey]);

  return (
    <AnalyticsContext.Provider value={importAddAnalytics}>
      <SettingPage
        title={intl.formatMessage({
          id: "settings.newImports.title",
          defaultMessage: "New Import",
          description: "Title of settings import page",
        })}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div>
            <div className={styles.panel}>
              <Collapse
                accordion
                activeKey={activePanelKey}
                expandIconPosition="end"
                onChange={handleCollapsePanelClick}
              >
                <Collapse.Panel
                  className={activePanelKey ? styles.panelTextBackground : styles.panelText}
                  header={intl.formatMessage(messages.collapsePanelTitle)}
                  key="1"
                >
                  {activePanelKey && <GetStartedSection />}
                </Collapse.Panel>
              </Collapse>
            </div>
            <PageContainer
              footer={
                step !== StepsEnum.FINALIZE && (
                  <ButtonBar
                    nextButtonConfirmationCancelText={
                      isNewConfig ? intl.formatMessage(messages.continueWithoutSaving) : undefined
                    }
                    nextButtonConfirmationMessage={nextButtonConfirmationMessage}
                    nextButtonConfirmationOkText={
                      isNewConfig ? intl.formatMessage(messages.saveMatching) : undefined
                    }
                    nextButtonErrorMessage={nextButtonErrorMessage}
                    nextDisabled={nextStepDisabled}
                    onConfirmationCancel={isNewConfig ? handleNextStep : undefined}
                    onConfirmationOk={isNewConfig ? showCreateConfigModal : undefined}
                    onNextClick={handleNextStep}
                    onPreviousClick={handlePreviousStep}
                    previousHidden={step === StepsEnum.UPLOAD}
                  />
                )
              }
            >
              <div>
                <Steps current={step} size="small">
                  {steps.map((title, index) => (
                    <Steps.Step
                      key={title}
                      subTitle={
                        index === 1 ? (
                          <Button
                            className={styles.stepInfo}
                            icon={<FontAwesomeIcon icon={faInfoCircle} />}
                            iconOnly
                            onClick={() => showInfo()}
                            type="link"
                          />
                        ) : undefined
                      }
                      title={title}
                    />
                  ))}
                </Steps>
              </div>
              <div className={styles.stepContainer}>
                {step === StepsEnum.UPLOAD && <UploadStep />}
                {step === StepsEnum.MATCH_FIELDS && <FieldMappingStep className={styles.step} />}
                {step === StepsEnum.FINALIZE && <FinalizeStep />}
              </div>
              {visible && <StepInfo onClose={() => hideInfo()} />}
              <CreateConfigModal
                okText={intl.formatMessage(messages.createConfigModalOkText)}
                onCancel={isNewConfig ? handleCancelConfigCreation : undefined}
                onCreate={isNewConfig ? handleNextStep : undefined}
                onHide={hideCreateConfigModal}
                visible={createConfigModalVisible}
              />
            </PageContainer>
            {layoutSelectorVisible && <LayoutSelectorModal onHide={handleHideLayoutSelector} />}
          </div>
        )}
      </SettingPage>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getEntityType(state),
  filePreview: getFilePreview(state),
  isConfigModified: isConfigModified(state),
  isNewConfig: !getSelectedConfig(state),
  loading: isAddPageLoading(state),
  mappedColumns: getMappedColumns(state),
  step: getStep(state),
});

const mapDispatchToProps = {
  initialize: initializeAddPage.request,
  setStep,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewImport);
